import React, {useState} from 'react';
import {Trans} from '@lingui/macro';
import cls from 'classnames';
import {i18n} from '../../../services/i18n';

import CompletedIcon from '@components/Icons/CompletedIcon';
import FlatButton from '@components/FlatButton/FlatButton';
import ChevronRightIcon from '@components/Icons/ChevronRightIcon';

import useMediaQuery from '../../../hooks/useMediaQuery';

import s from './LinkedQuestion.css';

export default function LinkedQuestions({
  isSelfEval,
  total,
  count,
  question,
  totalPage,
  currPage,
  onSelect,
  currentColumnIndex,
  onColumnChange,
}) {
  const isMobile = true; //useMediaQuery('(max-width: 900px)');

  const columns = question.getIn(['columns']);
  const answers = question.getIn(['answers']);

  // Unified select handler
  const handleSelect = (answerId, value, isSelected) => {
    const newValue = isSelected ? null : value;
 
    onSelect({
      answerId,
      value: newValue,
    });
  };

  const handleNext = () => {
    if (currentColumnIndex < columns.size - 1) {
      onColumnChange(currentColumnIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentColumnIndex > 0) {
      onColumnChange(currentColumnIndex - 1);
    }
  };
  return (
    <div className={s.ClosedQuestion}>
      <div className={cls(s.Flex, s.QuestionHeader)}>
        <div className={cls(s.Flex, s.QuestionCounter)}>
          <div className={s.Text}>
            <Trans id="closedQuestion.title" components={[<br />]} />
          </div>
          <div className={s.Counter}>
            <sup>{currPage}</sup>
            <span>/</span>
            <sub>{totalPage}</sub>
          </div>
        </div>
        {!isMobile && (
          <div className={cls(s.Flex, s.QuestionOptions)}>
            {columns.map(column => (
              <div key={column.get('key')}>{column.get('label') || column.get('key')}</div>
            ))}
          </div>
        )}
      </div>
      <MobileLinkedQuestionColumnView
        isSelfEval={isSelfEval}
        question={question}
        columns={columns}
        answers={answers}
        currentColumnIndex={currentColumnIndex}
        onSelect={handleSelect}
        handleNext={handleNext}
        handlePrevious={handlePrevious}
      />
    </div>
  );
}

const MobileLinkedQuestionColumnView = ({
  question,
  isSelfEval,
  columns,
  answers,
  currentColumnIndex,
  onSelect,
  handleNext,
  handlePrevious,
}) => {
  const minAnswers = question.get('min_answers');
  const maxAnswers = question.get('max_answers');
  const currentColumn = columns.get(currentColumnIndex);
  const isOnlyColumn = columns.size === 1;

  // Conta quante risposte sono già selezionate per la colonna corrente
  const selectedCountForCurrentColumn = answers.filter(
    a => a.get('value') === currentColumn.get('order')
  ).size;

  // Verifica se il limite massimo è stato raggiunto
  const isMaxReached = selectedCountForCurrentColumn >= maxAnswers;

  // Function to check if an answer has already been assigned to a different column
  const isAlreadySelected = answerId => {
    const answer = answers.find(a => a.get('id') === answerId);
    return answer && answer.get('value') && answer.get('value') !== currentColumn.get('order');
  };

  return (
    <div className={s.MobileQuestionContainer}>
      <div className={s.MobileQuestionHeader}>
        {isSelfEval ? (
          isOnlyColumn ? (
            <Trans id="linked_question.mobile_text_only_column" />
          ) : (
            <Trans
              id="linked_question.mobile_text"
              values={{minAnswers, column: currentColumn.get('label')}}
            />
          )
        ) : isOnlyColumn ? (
          <Trans id="linked_question.mobile_text_only_column_etero" />
        ) : (
          <Trans
            id="linked_question.mobile_text_etero"
            values={{minAnswers, column: currentColumn.get('label')}}
          />
        )}
      </div>

                     <ul className={s.MobileOptionsList} role="list">
        {answers.map((answer, index) => {
          const isSelected = answer.get('value') === currentColumn.get('order');
          const isAssignedToOtherColumn = isAlreadySelected(answer.get('id'));
          
          // Disabilita se:
          // 1. È già assegnato a un'altra colonna
          // 2. Non è selezionato E il limite massimo è stato raggiunto
          const isDisabled = isAssignedToOtherColumn || (!isSelected && isMaxReached);
          
          // Trova la colonna a cui è assegnata questa risposta
          const assignedColumnOrder = answer.get('value');
          const assignedColumn = assignedColumnOrder 
            ? columns.find(col => col.get('order') === assignedColumnOrder)
            : null;
              // Costruisci l'etichetta dello stato per lo screen reader
          let ariaLabel = answer.get('label');
          if (isAssignedToOtherColumn && assignedColumn) {
            ariaLabel += `, ${i18n._('linked_question.option_assigned_to_column', {column: assignedColumn.get('label')})}`;
          } else if (isSelected) {
            ariaLabel += `, ${i18n._('linked_question.option_selected')}`;
          } else if (isMaxReached) {
            ariaLabel += `, ${i18n._('linked_question.option_max_reached')}`;
          } else {
            ariaLabel += `, ${i18n._('linked_question.option_not_selected')}`;
          }
          
          return (
            <li
              key={answer.get('id')}
              className={s.MobileOptionItem}
              role="listitem"
            >
              <button
                type="button"
                className={cls(
                  s.MobileOptionLabel,
                  isSelected && s.SelectedOption,
                  isDisabled && s.DisabledOption
                )}
                onClick={() => {
                  if (!isDisabled) { 
                    onSelect(answer.get('id'), currentColumn.get('order'), isSelected);
                  }
                }}
                disabled={isDisabled}
                aria-label={ariaLabel}
              >
                <span className={s.OptionText} aria-hidden="true">{answer.get('label')}</span>
                {isSelected && (
                  <div className={s.Checkmark} aria-hidden="true">
                    <CompletedIcon className={s.CheckImage} />
                  </div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const DesktopLinkedQuestionView = ({question, onSelect}) => {
  return (
    <div className={s.QuestionContainer}>
      {question.getIn(['answers']).map(answer => (
        <div
          key={answer.get('id')}
          role="group"
          aria-labelledby={`question-${answer.get('id')}`}
          className={cls(s.Flex, s.QuestionWrapper)}
        >
          <div id={`question-${answer.get('id')}`} className={s.Question}>
            {answer.get('label')}
          </div>
          <div className={cls(s.Flex, s.QuestionOptions)}>
            {question.getIn(['columns']).map((column, answerIndex) => (
              <React.Fragment key={answerIndex}>
                <label
                  className="visually-hidden"
                  htmlFor={`question-${answer.get('id')}-${answerIndex}`}
                >
                  {column.get('label') || column.get('key')}
                </label>
                <input
                  id={`question-${answer.get('id')}-${answerIndex}`}
                  type="radio"
                  name={`answer_${answer.get('id')}`}
                  value={column.get('order')}
                  checked={answer.get('value') === column.get('order')}
                  onClick={() =>
                    onSelect({
                      answerId: answer.get('id'),
                      value:
                        answer.get('value') === column.get('order') ? null : column.get('order'),
                    })
                  }
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
